<template>
    <div>
        <!-- filter -->
        <v-row class="ma-1">
            <!-- city -->
            <v-col cols="2" class="pa-1">
                <v-autocomplete 
                    v-model="cityIds"
                    :items="cities"
                    item-text="name"
                    item-value="id"
                    :label="$t('inputs.cities')"
                    dense
                    outlined
                    hide-details
                    multiple
                    clearable
                    :disabled="loading"
                    :search-input.sync="searchCityId"
                    @change="searchCityId = null"
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        >
                            (+{{ cityIds.length - 1 }})
                        </span>
                    </template> 
                </v-autocomplete>
            </v-col>
            <!-- regions -->
            <v-col cols="2" class="pa-1">
                <v-autocomplete 
                    v-model="regionIds"
                    :items="cityIds.length ? getRegionsByCities : regionsAll"
                    item-text="name"
                    item-value="id"
                    :label="$t('inputs.regions')"
                    dense
                    outlined
                    hide-details
                    multiple
                    clearable
                    :disabled="loading"
                    :search-input.sync="searchRegion"
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        >
                            (+{{ regionIds.length - 1 }})
                        </span>
                    </template> 
                </v-autocomplete>
            </v-col>
            <!-- employees -->
            <v-col cols="3" class="pa-1">
                <v-autocomplete 
                    v-model="employeeIds"
                    :items="medicalRepsBrief"
                    item-text="name"
                    item-value="id"
                    :label="$t('inputs.employees')"
                    dense
                    outlined
                    hide-details
                    multiple
                    clearable
                    :disabled="loading"
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        >
                            (+{{ employeeIds.length - 1 }})
                        </span>
                    </template> 
                </v-autocomplete>
            </v-col>
            <!-- date -->
            <v-col cols="3" class="pa-1">
                <bee-date-input
                    v-model="date"
                    :append-icon="$i18n.locale === 'en' ? 'mdi-menu-right' : 'mdi-menu-left' "
                    :prepend-icon="$i18n.locale === 'en' ? 'mdi-menu-left' : 'mdi-menu-right' "
                    :label="$t('inputs.date')"
                    input-format-order="DD-MM-YYYY"
                    dense
                    :loading="loading"
                    hide-details
                    @click:append="
                        addDate();
                        filter()
                    "
                    @click:prepend="
                        subDate();
                        filter()
                    "
                    clearable
                    :disabled="loading"
                ></bee-date-input>
            </v-col>

            <v-spacer />
            <v-col cols="auto" class="pa-1 d-flex justify-end" align-self="center">
                <v-btn
                    color="success"
                    class="px-sm-4 mx-2"
                    :loading="loading"
                    :disabled="loading"
                    small
                    @click="isSearch = true; filter()"
                >
                    <v-icon class="me-1"> mdi-magnify </v-icon>
                    {{$t('search')}}
                </v-btn>
        
                <v-btn
                    class="px-sm-8"
                    color="alert-color white--text"
                    small
                    :disabled="loading"
                >
                    {{ $t('reset') }}
                </v-btn>
            </v-col>
        </v-row>

        <!-- table -->
        <bee-handy-table
            v-if="isSearch"
            :headers="headers"
            :items="materialsStockByCity"
            dense
            zebra
            :loading="loading"
            :data-table-props="{
                singleExpand: true,
				expandedSync: expanded,
				showExpand: true,
                itemKey: 'cityId',
                disableSort: true
			}"
            :items-per-page="-1"
            pagination-on-scroll
            :height="$vuetify.breakpoint.height - 170"
        >
            <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="tab" >
                    <bee-handy-table
						:headers="head"
						:items="item.materials"
						hide-default-footer
						class="tab"
						table-class="baby-blue table-bg rounded-0 elevation-0"
                        zebra
                        :data-table-props="{
                            singleExpand: true,
                            expandedSync: expanded,
                            showExpand: true,
                            itemKey: 'materialId',
                            disableSort: true
			            }"
                        pagination-on-scroll
                        height="300"
                        :items-per-page="-1"
					>
                        <template v-slot:expanded-item="{ headers, item }">
                            <td :colspan="headers.length" class="tab" >
                                <bee-handy-table
                                    :headers="headEmployees"
                                    :items="item.employees"
                                    hide-default-footer
                                    class="tab"
                                    table-class="table-bg rounded-0 elevation-0"
                                    zebra
                                    :data-table-props="{
                                        itemKey: 'employeeId',
                                        disableSort: true
                                    }"
                                    pagination-on-scroll
                                    height="300"
                                    :items-per-page="-1"
                                ></bee-handy-table>
                            </td>
                        </template>
                    </bee-handy-table>
                </td>
            </template>
        </bee-handy-table>

        <div
			v-if="!isSearch"
			class="d-flex flex-column justify-center align-center"
			:style="{ height: `${$vuetify.breakpoint.height - 210}px` }"
		>
			<template>
				<v-icon size="60" class="mb-1">mdi-invoice-list</v-icon>
				<span class="text-h6">{{$t('WarehousesStatistics.please-select-a-filter')}}</span>
			</template>
		</div>
    </div>
</template>

<script>
import moment from 'moment'
import { isDisabledFriday, enableVacations } from '@/configs/global.config'
import { mapState } from 'vuex'
export default {
    data () {
        return {
            cityIds: [],
            regionIds: [],
            employeeIds: [],
            date: moment().format('DD-MM-YYYY'),
            isDisabledFriday,
            enableVacations,
            loading: false,
            isSearch: false,
            materialsStockByCity: [],
            expanded: false,
            expandedTwoTable: false,
            searchCityId: null,
            searchRegion: null
        }
    },
    computed: {
        ...mapState({
            medicalRepsBrief: state => state.medicalreps.medicalRepsBrief,
            regionsAll: state => state.regions.regionsAll,
            cities: state => state.cities.cities,
            myMaterials: state => state.materials.myMaterials,
            vacationsDayDetails: state => state.vacations.vacationsDayDetails
        }),
        headers () {
            return [
                {
                    text: this.$t('headers.city'),
                    name: 'cityId',
                    value: 'cityId',
                    setValueExpr: val => this.cities.find(c => c.id === val)?.name
                }
            ]
        },

        head () {
            return [
                {
                    text: this.$t('headers.item'),
                    name: 'materialId',
                    value: 'materialId',
                    setValueExpr: val => this.myMaterials.find(c => c.id === val)?.name
                },
                {
                    text: this.$t('headers.total-amount'),
                    name: 'totalAmount',
                    value: 'totalAmount'
                }
            ]
        },

        headEmployees () {
            return [
                {
                    text: this.$t('headers.employee'),
                    name: 'employeeId',
                    value: 'employeeId',
                    setValueExpr: val => this.medicalRepsBrief.find(c => c.id === val)?.name
                },
                {
                    text: this.$t('headers.amount'),
                    name: 'amount',
                    value: 'amount'
                }
            ]
        },

        getRegionsByCities () {
            const regionsByCity = []
            for (let i = 0; i < this.cityIds.length; i++) {
                regionsByCity.push(
                    ...this.regionsAll.filter(c => c.cityId === this.cityIds[i])
                )
            }
            console.log(regionsByCity)
            return regionsByCity
        }
    },

    methods: {
        moment,
        filter () {
            this.loading = true
            this.$store.dispatch('statistics/fetchMaterialsStockByCity', { 
                cityIds: this.cityIds,
                regionIds: this.regionIds,
                employeeIds: this.employeeIds,
                date: this.date 
            }).then((data) => {
                this.materialsStockByCity = data
            }).finally(() => {
                this.loading = false
            })
        },
        addDate () {
            this.date = moment(this.date).add(1, 'day').format('YYYY-MM-DD');
            if (
                (moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) ||
                (this.enableVacations && this.vacationsDayDetails.includes(this.date))
            ) {
                this.addDate()
            }
            return this.date
        },
        subDate() {
            this.date = moment(this.date).subtract(1, 'day').format('YYYY-MM-DD')
            if (
                (moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) || 
                (this.enableVacations && this.vacationsDayDetails.includes(this.date))
            ) {
                this.subDate()
            }
            return this.date
        }
    },

    created () {
        Promise.all([
            !this.cities.length ? this.$store.dispatch('cities/fetchCityMine', {}) : null,
            !this.medicalRepsBrief.length ? this.$store.dispatch('medicalreps/fetchAllBrief', {}) : null,
            !this.regionsAll.length ? this.$store.dispatch('regions/fetchAllRegions', {}) : null,
            !this.myMaterials.length ? this.$store.dispatch('materials/fetchMyMaterial', {}) : null,
            enableVacations && !this.vacationsDayDetails.length ? this.$store.dispatch('vacations/fetchAll') : null
        ]).then(() => {
            this.getRegionsByCities()
        })
    }

}
</script>